<template>
    <Teleport to="body">
        <Transition name="modal">
            <div
                v-if="isOpen"
                class="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
            >
                <!-- Backdrop -->
                <div class="absolute inset-0 bg-black/75 backdrop-blur-sm" @click="$emit('close')" />

                <!-- Modal -->
                <div
                    ref="modalRef"
                    class="relative w-full max-w-md max-h-[100dvh] sm:max-h-[90vh] flex flex-col bg-zinc-950 rounded-t-3xl sm:rounded-2xl border-t border-amber-400/20 sm:border border-amber-400/20 shadow-2xl overflow-hidden"
                    :style="modalStyle"
                    @touchstart="handleTouchStart"
                    @touchmove="handleTouchMove"
                    @touchend="handleTouchEnd"
                >
                    <!-- Cinematic gold glow backdrop -->
                    <div class="tenx-glow pointer-events-none absolute inset-0" aria-hidden="true"></div>

                    <!-- Mobile Pull Handle -->
                    <div class="sm:hidden flex justify-center pt-2 pb-0.5 cursor-grab active:cursor-grabbing relative z-10 shrink-0">
                        <div class="w-10 h-1 rounded-full bg-zinc-700" />
                    </div>

                    <!-- Close Button -->
                    <button
                        @click="$emit('close')"
                        class="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 z-20 w-8 h-8 rounded-lg bg-black/40 hover:bg-white/10 flex items-center justify-center transition-colors active:scale-95"
                        aria-label="Close"
                    >
                        <X class="w-4 h-4 text-white/70" />
                    </button>

                    <!-- Content -->
                    <div class="relative z-10 px-5 pt-4 pb-5 sm:px-6 sm:pt-6 sm:pb-6 overflow-y-auto min-h-0">
                        <!-- Eyebrow Badge -->
                        <div class="flex justify-center mb-3 tenx-fade-in" style="animation-delay: 0ms">
                            <div class="inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-amber-300">
                                <span class="tenx-sparkle">✨</span>
                                Gold Member Bonus
                                <span class="tenx-sparkle" style="animation-delay: 0.4s">✨</span>
                            </div>
                        </div>

                        <!-- Headline -->
                        <div class="text-center mb-4 tenx-fade-in" style="animation-delay: 80ms">
                            <h2 class="text-white text-2xl sm:text-3xl font-bold leading-tight pr-6">
                                <span class="bg-clip-text text-transparent bg-gradient-to-r from-amber-300 via-yellow-300 to-amber-400">10X</span>
                                Every Star You Give
                            </h2>
                            <p class="text-white/60 text-xs sm:text-sm mt-1.5 leading-relaxed">
                                Upgrade to <span class="text-amber-300 font-semibold">Gold</span> and every star you spend on auditions counts as <span class="text-white font-semibold">ten</span>.
                            </p>
                        </div>

                        <!-- Dynamic Example Card -->
                        <div class="mb-5 tenx-fade-in" style="animation-delay: 160ms">
                            <div class="rounded-2xl border border-white/10 bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-950 overflow-hidden">
                                <!-- Top label -->
                                <div class="px-4 pt-3 pb-1 text-center">
                                    <p class="text-[11px] font-bold uppercase tracking-[0.18em] text-white/40">
                                        With your current balance
                                    </p>
                                </div>

                                <!-- Comparison row -->
                                <div class="flex items-stretch px-4 pb-4 pt-2 gap-2">
                                    <!-- Now -->
                                    <div class="flex-1 rounded-xl bg-white/[0.03] border border-white/[0.06] p-3 flex flex-col items-center justify-center">
                                        <p class="text-[10px] font-semibold uppercase tracking-wider text-white/40 mb-1.5">Now</p>
                                        <div class="flex items-center gap-1.5">
                                            <img :src="STAR_ICON" alt="" class="w-5 h-5 opacity-70" />
                                            <span class="text-2xl font-bold text-white/80 tabular-nums">{{ userBalance.toLocaleString() }}</span>
                                        </div>
                                        <p class="text-[10px] text-white/30 mt-0.5">star{{ userBalance === 1 ? '' : 's' }} per give</p>
                                    </div>

                                    <!-- Arrow -->
                                    <div class="flex items-center justify-center px-1">
                                        <div class="text-amber-400/80 text-2xl font-bold tenx-arrow-pulse">→</div>
                                    </div>

                                    <!-- With Gold -->
                                    <div class="flex-1 rounded-xl bg-gradient-to-br from-amber-500/[0.12] via-yellow-500/[0.08] to-amber-600/[0.12] border border-amber-400/30 p-3 flex flex-col items-center justify-center relative overflow-hidden">
                                        <div class="absolute inset-0 tenx-card-shimmer pointer-events-none"></div>
                                        <p class="text-[10px] font-semibold uppercase tracking-wider text-amber-300/90 mb-1.5 relative">With Gold</p>
                                        <div class="flex items-center gap-1.5 relative">
                                            <img :src="STAR_ICON" alt="" class="w-5 h-5 drop-shadow-[0_0_6px_rgba(251,191,36,0.6)]" />
                                            <span class="text-2xl font-bold tabular-nums bg-clip-text text-transparent bg-gradient-to-r from-amber-200 via-yellow-200 to-amber-300">{{ goldBalance.toLocaleString() }}</span>
                                        </div>
                                        <p class="text-[10px] text-amber-200/70 mt-0.5 relative">star{{ goldBalance === 1 ? '' : 's' }} per give</p>
                                    </div>
                                </div>

                                <!-- Footnote -->
                                <div class="border-t border-white/[0.04] px-4 py-2.5 text-center bg-black/20">
                                    <p class="text-[11px] text-white/50 leading-snug">
                                        Same {{ userBalance.toLocaleString() }} stars from your wallet —
                                        <span class="text-amber-300 font-semibold">10× the impact</span>
                                        on the audition.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <!-- CTA — simple hand-off to the /gold lander. The button is
                             intentionally pricing-free: the lander owns the closing pitch
                             (plans, billing disclosure, comparison to the live tour) so we
                             don't duplicate that copy here AND because two surfaces of
                             pricing truth would drift the moment Gold runs a promo. The
                             "See pricing and full Gold benefits" sub-line tells the user
                             this opens another page rather than committing them to
                             checkout. -->
                        <div class="tenx-fade-in" style="animation-delay: 240ms">
                            <a
                                :href="goldHref"
                                @click="handleUpgradeClick"
                                class="tenx-cta"
                            >
                                <span>Upgrade to Gold</span>
                            </a>
                            <p class="text-center text-[11px] text-white/40 mt-2.5">
                                See pricing and full Gold benefits
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { ref, computed } from 'vue'
import { X } from 'lucide-vue-next'
import { trackFathomEvent, getFathomPostPrefix } from '@/Helpers/analytics'

const STAR_ICON = 'https://sm.naughtycdn.com/images/landers/prize/NA-Star.png'

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true,
    },
    userBalance: {
        type: Number,
        required: true,
    },
    routePrefix: {
        type: String,
        default: '',
    },
    // ?source= value tagged onto the /gold URL the CTA navigates to. The
    // /gold lander's $goldCustom3Sanitize block (naGoldNew.blade.php) mirrors
    // this (plus videoId, if supplied) into custom3 on every downstream
    // checkout — that's how admincp's /gold-sales + /gold-checkouts pivot
    // conversions back to "started from the 10X bonus modal".
    source: {
        type: String,
        default: 'tenx_bonus_modal',
    },
    // Which CTA opened this modal — 'sidebar' or 'modal'. Used to suffix the
    // Fathom upgrade-click event AND to tag the /gold URL's `ref=` so the
    // lander records WHICH bonus-modal surface a buyer reached it from
    // (Fathom covers click volume; the ref tag flows through GoldEntry-
    // RefererComposer + processing_sessions.referer for backend reporting
    // when source is non-empty and overrides ref in custom3).
    openedFrom: {
        type: String,
        default: 'sidebar',
    },
    // Current audition's feed_post.id, used as ?video_id= on the /gold URL
    // so the lander composes custom3 = `${source}_${video_id}` on every
    // downstream checkout. NULL when the parent didn't supply one (e.g.
    // before currentVideoData has resolved); the CTA still works without it.
    videoId: {
        type: [Number, String],
        default: null,
    },
})

const emit = defineEmits(['close'])

const fathomPrefix = computed(() => getFathomPostPrefix(props.routePrefix))

const goldBalance = computed(() => props.userBalance * 10)

// Builds the /gold lander URL the CTA navigates to. Tagging:
//   - source: 'tenx_bonus_modal' — identifies the 10X bonus modal as the
//     entry surface; the lander mirrors it into custom3 on every checkout.
//   - ref: 'sidebar' | 'gs_modal' — the originating CTA inside the feed
//     (sidebar 10X banner vs. give-stars modal's gold-bonus tile). Not
//     consumed by custom3 derivation (source wins) but visible in the URL
//     for ad-hoc inspection and forwarded as the page's Referer signal.
//   - video_id: composes custom3 = `${source}_${video_id}` so admincp's
//     /gold-sales pivot pulls the source audition out of
//     processing_purchases.extra_data.custom3.
const goldHref = computed(() => {
    const refTag = props.openedFrom === 'modal' ? 'gs_modal' : 'sidebar'
    const params = {
        source: props.source,
        ref: refTag,
    }
    if (props.videoId !== null && props.videoId !== '') {
        params.video_id = props.videoId
    }
    try {
        return route('members2.account.naGold', params)
    } catch (e) {
        const qs = new URLSearchParams(params).toString()
        return '/gold' + (qs ? '?' + qs : '')
    }
})

const handleUpgradeClick = () => {
    // Suffix on the originating surface so we can compare upgrade-click
    // rates between users who reached the bonus modal from the sidebar
    // banner vs. from the give-stars modal's gold-bonus tile. Event name
    // is intentionally unchanged from the prior direct-checkout CTA so
    // existing Fathom dashboards keep working; the destination change is
    // observable in /gold-sales via the source=tenx_bonus_modal tag.
    const suffix = props.openedFrom === 'modal' ? 'FromModal' : 'FromSidebar'
    trackFathomEvent(`${fathomPrefix.value}_Tenx_Bonus_Upgrade_Clicked_${suffix}`)
}

// Touch/swipe to dismiss — same pattern used by GiveStarsModal so the
// gesture feels identical between the two sheets on mobile.
const modalRef = ref(null)
const touchStartY = ref(0)
const touchCurrentY = ref(0)
const isDragging = ref(false)

const modalStyle = computed(() => {
    if (!isDragging.value) return {}
    const deltaY = touchCurrentY.value - touchStartY.value
    if (deltaY < 0) return {}
    return {
        transform: `translateY(${deltaY}px)`,
        transition: 'none',
    }
})

const handleTouchStart = (e) => {
    if (!modalRef.value) return
    const touch = e.touches[0]
    const rect = modalRef.value.getBoundingClientRect()
    if (touch.clientY - rect.top > 60) return
    touchStartY.value = touch.clientY
    touchCurrentY.value = touch.clientY
    isDragging.value = true
}

const handleTouchMove = (e) => {
    if (!isDragging.value) return
    const touch = e.touches[0]
    touchCurrentY.value = touch.clientY
    if (touchCurrentY.value - touchStartY.value > 0) e.preventDefault()
}

const handleTouchEnd = () => {
    if (!isDragging.value) return
    if (touchCurrentY.value - touchStartY.value > 100) emit('close')
    isDragging.value = false
    touchStartY.value = 0
    touchCurrentY.value = 0
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}
.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.tenx-glow {
    background:
        radial-gradient(60% 50% at 50% 0%, rgba(251, 191, 36, 0.18), transparent 70%),
        radial-gradient(50% 40% at 100% 100%, rgba(245, 158, 11, 0.14), transparent 70%),
        radial-gradient(40% 30% at 0% 100%, rgba(252, 211, 77, 0.10), transparent 70%);
    animation: tenxGlowDrift 12s ease-in-out infinite alternate;
}
@keyframes tenxGlowDrift {
    0%   { transform: translateY(0) scale(1); opacity: 0.85; }
    100% { transform: translateY(-2%) scale(1.05); opacity: 1; }
}

.tenx-fade-in {
    opacity: 0;
    transform: translateY(8px);
    animation: tenxFadeUp 0.6s ease-out forwards;
}
@keyframes tenxFadeUp {
    to { opacity: 1; transform: translateY(0); }
}

.tenx-sparkle {
    display: inline-block;
    animation: tenxSparkle 1.6s ease-in-out infinite;
}
@keyframes tenxSparkle {
    0%, 100% { opacity: 0.6; transform: scale(0.9); }
    50%      { opacity: 1; transform: scale(1.15); }
}

.tenx-arrow-pulse {
    animation: tenxArrowPulse 1.8s ease-in-out infinite;
}
@keyframes tenxArrowPulse {
    0%, 100% { transform: translateX(0); opacity: 0.7; }
    50%      { transform: translateX(3px); opacity: 1; }
}

.tenx-card-shimmer {
    background: linear-gradient(
        110deg,
        transparent 30%,
        rgba(252, 211, 77, 0.10) 50%,
        transparent 70%
    );
    background-size: 200% 100%;
    animation: tenxShimmer 3.5s ease-in-out infinite;
}
@keyframes tenxShimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
}

/* ── CTA button. Gold gradient matches naGoldNew.blade.php's hero-modal
   primary plan pill so the visual language stays familiar to anyone who's
   seen the lander. We keep the gradient + glow + lift transition but drop
   the price/period/crown sub-elements because this modal hands off to the
   /gold page rather than closing the sale itself. ── */
.tenx-cta {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 13px 18px;
    border-radius: 12px;
    background: linear-gradient(90deg, #b8860b 0%, #d4a830 50%, #c9a84c 100%);
    border: 2px solid rgba(240, 208, 120, 0.5);
    color: #000;
    font-size: 13px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    text-decoration: none;
    transition: background 0.2s, border-color 0.2s, transform 0.15s, box-shadow 0.2s;
    cursor: pointer;
}
.tenx-cta:hover {
    background: linear-gradient(90deg, #c9960c 0%, #e0b838 50%, #d4b256 100%);
    border-color: rgba(240, 208, 120, 0.8);
    color: #000;
    text-decoration: none;
    transform: translateY(-1px);
    box-shadow: 0 0 24px rgba(240, 208, 120, 0.3);
}
.tenx-cta:active {
    transform: translateY(0);
}
</style>
